import {
  useModals,
  Button,
  ButtonLink,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { TimeOffHistoryChart } from "@/pages/time-off/components/TimeOffHistoryChart.tsx";
import { TimeOffHistoryDetailsModalProps } from "@/pages/time-off/widgets/TimeOffHistoryModal.tsx";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPeriodTrackingTime,
  getAccrualFrequencyTypes,
} from "@/types/time-off/time-off-policy.types.ts";
import { ApiTimeOffHistory, ApiTimeOffHistoryLogsType } from "@/types/time-off/time-off.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface TimeOffHistoryGraphProps extends TimeOffHistoryDetailsModalProps {
  showLogs: (type?: ApiTimeOffHistoryLogsType) => void;
  showPeriod: (next: boolean) => void;
  history?: ApiTimeOffHistory | undefined;
}

export function TimeOffHistoryGraph({
  showLogs,
  showPeriod,
  employeeId,
  timeOffCategory,
  history,
  ...props
}: TimeOffHistoryGraphProps) {
  const { close } = useModals();
  const { id } = props;
  const { t } = useTranslation();
  const { renderDate, capitalizeFirstLetter } = useFormatter();
  const balance = history?.filter.time_off_category.balance.available_balance ?? "";
  const isHours =
    history?.filter.time_off_category.period_track_type === ApiTimeOffPeriodTrackingTime.hours;
  const pluralKey = isHours ? "plurals:plural_hours" : "plurals:plural_day";
  const accruedText = useMemo(() => {
    if (history?.filter.time_off_category.category_type === ApiTimeOffCategoryType.flexible)
      return "";

    const frequencyType = getAccrualFrequencyTypes().find(
      (item) => item.id === history?.information?.accrual_frequency_type
    );

    if (!history?.information) return "";

    return t("time_off:accrual_information", {
      amount: t(pluralKey, {
        count: history?.information?.accrual_amount,
      }),
      period: frequencyType?.title.toLowerCase() ?? "",
    });
  }, [history]);
  const cycleText =
    history && history.information
      ? `${renderDate(history.information.date_cycle_start, "MMM yyyy")} - ${renderDate(history.information.date_cycle_end, "MMM yyyy")}`
      : "";

  const filterPeriodText = history
    ? `${renderDate(history.filter.date_start, "MMM yyyy")} - ${renderDate(history.filter.date_end, "MMM yyyy")}`
    : `${renderDate(new Date(), "yyyy")}`;

  return (
    <>
      <Stack gap={"md"}>
        <Stack>
          <Text size={"lg"}>{t("time_off:available_today")}</Text>
          <Title
            header={
              history?.filter.time_off_category.category_type !== ApiTimeOffCategoryType.flexible
                ? t(pluralKey, {
                    count: parseFloat(balance),
                  })
                : parseFloat(balance) > 0
                  ? t(pluralKey, {
                      count: parseFloat(balance),
                    })
                  : "♾️"
            }
            size={"lg"}
            caption={accruedText}
          />
        </Stack>
        <Stack gap={"xs"} direction={"horizontal"}>
          <Button
            leftIcon={<FontAwesomeIcon icon="fa-light fa-angle-left" />}
            variant={"secondary"}
            onClick={() => {
              showPeriod(false);
            }}
          />
          <Button variant={"secondary"} disabled={true} className={"pointer-events-none"}>
            {filterPeriodText}
          </Button>
          <Button
            leftIcon={<FontAwesomeIcon icon="fa-light fa-angle-right" />}
            variant={"secondary"}
            onClick={() => {
              showPeriod(true);
            }}
          />
        </Stack>

        <Stack gap={"lg"}>
          {history && <TimeOffHistoryChart history={history} />}

          <Stack gap={"xs"}>
            <ButtonLink
              href={`/time-off/request/new?employeeId=${employeeId}&categoryId=${timeOffCategory.id}`}
              size={"lg"}
              target={"_blank"}
              onClick={() => {
                close(id);
              }}
            >
              {t("time_off:request_time_off", {
                category_name: history?.filter.time_off_category.title,
              })}
            </ButtonLink>
            <Button
              size={"lg"}
              variant={"secondary"}
              onClick={() => {
                showLogs();
              }}
            >
              {t("common:view_history")}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {history?.earnings && (
        <>
          <Title
            size={"md"}
            header={t("time_off:earnings")}
            caption={cycleText}
            paddingTop={true}
            paddingBottom={true}
          />
          <ListItem
            className="cursor-pointer"
            onClick={() => {
              showLogs(ApiTimeOffHistoryLogsType.earnings);
            }}
            caption={t("time_off:accrued_by_now")}
            valueSlot={
              <Stack items={"center"} gap={"xs"} direction={"horizontal"}>
                <Text>
                  {t(pluralKey, {
                    count: history.earnings.accrual_current_amount,
                  })}
                </Text>
                <FontAwesomeIcon icon="fa-light fa-angle-right" />
              </Stack>
            }
          />
          <ListItem
            caption={t("time_off:carried_from_previous_period")}
            value={t(pluralKey, {
              count: history.earnings.carryover_from_previous_cycle_amount,
            })}
          />
          <ListItem
            caption={t("time_off:accrued_total_in_period")}
            value={t(pluralKey, {
              count: history.earnings.accrual_for_cycle_amount,
            })}
          />
        </>
      )}

      {history?.spendings && (
        <>
          <Title
            size={"md"}
            header={t("time_off:spendings")}
            caption={cycleText}
            paddingTop={true}
            paddingBottom={true}
          />
          <ListItem
            className="cursor-pointer"
            onClick={() => {
              showLogs(ApiTimeOffHistoryLogsType.spendings);
            }}
            caption={t("time_off:used")}
            valueSlot={
              <Stack items={"center"} gap={"xs"} direction={"horizontal"}>
                <Text>{t(pluralKey, { count: history.spendings.used_amount })}</Text>
                <FontAwesomeIcon icon="fa-light fa-angle-right" />
              </Stack>
            }
          />
          <ListItem
            caption={t("time_off:booked")}
            value={t(pluralKey, { count: history.spendings.booked_amount })}
          />
        </>
      )}

      {history?.information && (
        <>
          <Title
            size={"md"}
            header={t("time_off:dont_forget")}
            paddingTop={true}
            paddingBottom={true}
          />
          {history?.information?.accrual_cap_enabled && (
            <ListItem
              caption={t("time_off:accrual_cap_title")}
              value={t(pluralKey, { count: history.information.accrual_cap_amount })}
            />
          )}
          <ListItem
            caption={t("time_off:transfer_unused_balance")}
            value={capitalizeFirstLetter(
              history?.information?.carryover_enabled
                ? t("time_off:available")
                : t("common:disabled")
            )}
          />
        </>
      )}
    </>
  );
}
